import { Preview, Refresh, Search, Warning } from "@mui/icons-material"
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import { DataGridProps } from "@mui/x-data-grid"
import { TFunction } from "i18next"
import { get } from "lodash"
import { FC, useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import { SharedAccessPatternSaEnum } from "shared/sharedAccessPatternsData"
import { Tenant } from "shared/tenant"
import { TenantThingTypeMrn } from "shared/tenantData"
import { UserThingTypeMrn } from "shared/userData"
import { selectSaList, thingsActions } from "src/features/things/thingsSlice"
import { useAppDispatch, useAppSelector } from "../app/hooks"
import { selectMeIsReady } from "../features/me/meSlice"
import { useGetThingsQuery } from "../features/things/thingsApi"
import { ObjectDisplayDrawer } from "./ObjectDisplay"
import SaLayout from "./SaLayout"
import SaOrgForm from "./SaOrgForm"
import { Sl2Btn } from "./Sl2Btn"
import Sl2DataGrid from "./Sl2DataGrid"

const pageSize = 100

const WarningInfo: FC<{ tenant: Tenant }> = ({ tenant }) => {
  return (
    <Stack direction="column" spacing={1} alignItems="center">
      <Typography variant="body2" fontWeight="bold" noWrap>
        Missing configuration
      </Typography>
      <Typography variant="body2" noWrap>
        SMS Expeditor
      </Typography>

      <Typography variant="body2" noWrap>
        Email Expeditor
      </Typography>
    </Stack>
  )
}

const defaultColWidth = 230

export const SaTenantsPage: FC = () => {
  const { t } = useTranslation()

  const [snackMessage, setSnackMessage] = useState({
    message: "",
    severity: "success" as "success" | "error" | "warning" | "info" | undefined,
  })

  const isReady = useAppSelector(selectMeIsReady)

  const [isCreating, setIsCreating] = useState(false)

  const [localSearch, setLocalSearch] = useState<Record<string, string>>({})

  const [remoteSearch, setRemoteSearch] = useState<Record<string, string>>({})

  const qGetUsers = useGetThingsQuery({
    params: {
      "ap.name": SharedAccessPatternSaEnum.SaGetUsers,
    },
  })

  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(thingsActions.clearSaList())
  }, [dispatch])

  const onSearchClick = useCallback(() => {
    dispatch(thingsActions.clearSaList())
    // prefix "ap." on all keys
    const newRemoteSearch = {} as any
    // Object.keys(localSearch).forEach((key) => {
    //   newRemoteSearch[`ap.${key}`] = localSearch[key]
    // })
    if (localSearch.tenantName) {
      newRemoteSearch["ap.tenantName"] = `${localSearch.tenantName}`
    }
    if (localSearch.id) {
      newRemoteSearch["ap.ids[0]"] = localSearch.id
    }
    if (localSearch.mblsProviderId) {
      const n = Number(localSearch.mblsProviderId)
      if (!Number.isFinite(n)) {
        setSnackMessage({
          message: "Provider ID must be a number",
          severity: "error",
        })
        return
      }
      newRemoteSearch["ap.mblsProviderIds[0]"] = String(n)
    }

    setRemoteSearch(newRemoteSearch)
    setParams((prev) => {
      return {
        ...prev,
        ...newRemoteSearch,
        "ap.nextToken": undefined,
        timestamp: Date.now(),
      }
    })
  }, [
    dispatch,
    localSearch.id,
    localSearch.mblsProviderId,
    localSearch.tenantName,
  ])

  const [params, setParams] = useState<Record<string, any>>({
    "ap.name": SharedAccessPatternSaEnum.SaGetTenants,
    "ap.scanIndexForward": false,
    // "ap.tenantOnly": true,
    "ap.limit": 2000,
    "ap.nextToken": undefined,
    ...remoteSearch,
    timestamp: Date.now(),
  })

  const q = useGetThingsQuery({ params })

  // const { isLoading, isFetching, refetch, data, isSuccess, isError } =
  //   useGetThingsQuery({
  //     params,
  //   })

  useEffect(() => {
    if (q.isFetching || q.isLoading) return
    // handle snackbar message, error, success, etc
    if (q.isError) {
      setSnackMessage({ message: "Error fetching tenants", severity: "error" })
    } else if (q.isSuccess) {
      setSnackMessage({ message: "Tenants fetched", severity: "success" })
    }
  }, [q.isError, q.isFetching, q.isLoading, q.isSuccess])

  // const tenants = useMemo(() => (data?.things || []) as any as Tenant[], [data])

  const saList = useAppSelector((state) => selectSaList<Tenant>(state))

  const tenants = useMemo(
    () => saList.filter((t) => t.thingTypeMrn === TenantThingTypeMrn),
    [saList],
  )

  const tenantIds = useMemo(() => tenants.map((t) => t.ownerId), [tenants])

  const owners = useMemo(
    () => saList.filter((t) => t.thingTypeMrn === UserThingTypeMrn),
    [saList],
  )

  const tenantsWithOwners = useMemo(() => {
    return tenants.map((t) => {
      return {
        ...t,
        owner: owners.find((o) => o.id === t.ownerId),
      }
    })
  }, [owners, tenants])

  const onFinish = useCallback(() => {
    q.refetch()
    setIsCreating(false)
  }, [q])

  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null)

  const columns = useCallback(
    (t: TFunction): DataGridProps["columns"] =>
      [
        {
          field: "name",
          headerName: t("name"),
          minWidth: defaultColWidth,
          renderCell: (params) => {
            const displayWarning =
              !params?.row?.smsExpeditorId || !params?.row?.emailExpeditorId
            return (
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                height={"100%"}
              >
                <Preview
                  sx={{
                    cursor: "pointer",
                    color: "gray",
                  }}
                  onClick={() => setSelectedTenantId(params.row.id as string)}
                />
                <Link to={`/sa/tenants/${params.row.id}`}>
                  <Typography variant="body2" noWrap>
                    {params.value}
                  </Typography>
                </Link>
                {displayWarning && (
                  <Tooltip
                    title={<WarningInfo tenant={params.row as Tenant} />}
                    placement="top-start"
                    arrow
                  >
                    <Warning color="warning" fontSize="small" />
                  </Tooltip>
                )}
              </Stack>
            )
          },
        },
        {
          field: "description",
          headerName: t("description"),
          minWidth: defaultColWidth,
        },
        {
          field: "status",
          headerName: t("status"),
          minWidth: defaultColWidth,
          renderCell: (params) => {
            return (
              <Chip
                label={params.value}
                variant="outlined"
                size="small"
                color={params.value === "active" ? "primary" : "warning"}
              />
            )
          },
        },
        {
          field: "notifTemplateReadOnly",
          headerName: t("notifTemplateReadOnly"),
          minWidth: defaultColWidth,
          renderCell: (params) => {
            return (
              <Chip
                label={params.value ? "Yes" : "No"}
                variant={params.value ? "filled" : "outlined"}
                color={params.value ? "primary" : "default"}
              />
            )
          },
        },
        // appliedTemplatePreset
        {
          field: "appliedTemplatePreset",
          headerName: t("appliedTemplatePreset"),
          minWidth: defaultColWidth,
          renderCell: (params) => {
            if (!params.value) {
              return "-"
            }
            return (
              <Chip label={params.value} variant={"filled"} color={"primary"} />
            )
          },
        },

        {
          field: "owner",
          headerName: t("owner"),
          minWidth: defaultColWidth,
          renderCell: (params) => {
            const id = get(params, "row.ownerId")
            if (!id) return <Stack>{t("unknown")}</Stack>
            const email = get(params, "row.owner.email")
            const firstName = get(params, "row.owner.firstName")
            const lastName = get(params, "row.owner.lastName")
            return (
              <Stack direction="column" spacing={0}>
                <Typography variant="body1">{`${firstName} ${lastName}`}</Typography>
                <Typography variant="body2">
                  {" "}
                  <a href={`mailto:${email}`} target="_blank" rel="noreferrer">
                    {email}
                  </a>
                </Typography>
              </Stack>
            )
          },
        },
        {
          field: "partnerName",
          headerName: t("partnerName"),
          minWidth: defaultColWidth,
        },
        {
          field: "mblsProviderId",
          headerName: t("mblsProviderId"),
          minWidth: defaultColWidth,
        },
        {
          field: "contactName",
          headerName: t("contactName"),
          minWidth: defaultColWidth,
        },
        {
          field: "contactEmail",
          headerName: `✉️ ${t("contactEmail")}`,
          minWidth: defaultColWidth,
          renderCell: (params) => {
            return (
              <a
                href={`mailto:${params.value}`}
                target="_blank"
                rel="noreferrer"
              >
                {params.value}
              </a>
            )
          },
        },

        {
          field: "createdAt",
          headerName: `📅 ${t("createdAt")}`,
          minWidth: defaultColWidth,
        },
        {
          field: "updatedAt",
          headerName: `📅 ${t("updatedAt")}`,
          minWidth: defaultColWidth,
        },
      ] as DataGridProps["columns"],
    [],
  )
  const handleKeyPress = useCallback(
    (
      e:
        | React.KeyboardEvent<HTMLDivElement>
        | React.KeyboardEvent<HTMLTextAreaElement>
        | React.KeyboardEvent<HTMLInputElement>,
    ) => {
      if (e.key === "Enter") {
        onSearchClick()
      }
    },
    [onSearchClick],
  )

  return (
    <SaLayout selectedMenuItemLabelKey="organizations">
      <Box
        sx={{
          maxWidth: 1400,
          mx: "auto",
          px: { xs: 3, md: 5 },
          py: { xs: 4, md: 5 },
          width: "100%",
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-end"
          flexWrap="wrap"
          gap={2}
          sx={{ mb: 3 }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: "0.72rem",
                fontWeight: 600,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "primary.main",
              }}
            >
              {t("adminApp")}
            </Typography>
            <Stack direction="row" spacing={1.5} alignItems="baseline">
              <Typography
                variant="h4"
                sx={{
                  mt: 0.5,
                  fontWeight: 700,
                  letterSpacing: "-0.02em",
                  color: "text.primary",
                  lineHeight: 1.15,
                }}
              >
                {t("tenants")}
              </Typography>
              <Typography
                sx={{
                  color: "text.secondary",
                  fontSize: "1.1rem",
                  fontWeight: 500,
                }}
              >
                {tenants?.length ?? 0}
              </Typography>
            </Stack>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            {q.isLoading || q.isFetching ? (
              <CircularProgress size={22} />
            ) : (
              <IconButton
                onClick={q.refetch}
                size="small"
                sx={{
                  bgcolor: "rgba(35, 66, 133, 0.06)",
                  color: "primary.main",
                  "&:hover": { bgcolor: "rgba(35, 66, 133, 0.12)" },
                }}
              >
                <Refresh fontSize="small" />
              </IconButton>
            )}
            <Sl2Btn
              variant="contained"
              disableElevation
              onClick={() => setIsCreating(true)}
            >
              {t("createTenant")}
            </Sl2Btn>
          </Stack>
        </Stack>

        {/* Search card */}
        <Box
          sx={{
            bgcolor: "white",
            border: "1px solid",
            borderColor: "rgba(0, 0, 0, 0.08)",
            borderRadius: 2,
            boxShadow: "0 1px 2px rgba(35, 66, 133, 0.04)",
            p: { xs: 2, md: 2.5 },
            mb: 2.5,
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
            rowGap={1}
          >
            <TextField
              label={t("searchByName")}
              variant="outlined"
              fullWidth
              size={"small"}
              sx={{ flex: "1 1 220px" }}
              value={localSearch.tenantName}
              onChange={(e) =>
                setLocalSearch((prev) => ({
                  ...prev,
                  tenantName: e.target.value,
                }))
              }
              onKeyUp={handleKeyPress}
            />
            <TextField
              label={t("searchById")}
              variant="outlined"
              fullWidth
              size={"small"}
              sx={{ flex: "1 1 220px" }}
              value={localSearch.id}
              onChange={(e) =>
                setLocalSearch((prev) => ({
                  ...prev,
                  id: e.target.value,
                }))
              }
              onKeyUp={handleKeyPress}
            />
            <TextField
              label={t("searchByMblsProviderId")}
              variant="outlined"
              fullWidth
              size={"small"}
              sx={{ flex: "1 1 220px" }}
              value={localSearch.mblsProviderId}
              onChange={(e) =>
                setLocalSearch((prev) => ({
                  ...prev,
                  mblsProviderId: e.target.value,
                }))
              }
              onKeyUp={handleKeyPress}
            />
            <IconButton
              onClick={onSearchClick}
              sx={{
                bgcolor: "primary.main",
                color: "white",
                "&:hover": { bgcolor: "primary.dark" },
              }}
            >
              <Search fontSize="small" />
            </IconButton>
          </Stack>
        </Box>

        {/* Create form (conditional) */}
        {isCreating && (
          <Box data-ui="UI-SL2W-130"
            sx={{
              bgcolor: "white",
              border: "1px solid",
              borderColor: "rgba(0, 0, 0, 0.08)",
              borderRadius: 2,
              boxShadow: "0 1px 2px rgba(35, 66, 133, 0.04)",
              p: { xs: 2, md: 2.5 },
              mb: 2.5,
              maxWidth: 800,
            }}
          >
            <SaOrgForm
              isCreating={true}
              onFinish={onFinish}
              onCancel={() => setIsCreating(false)}
            />
          </Box>
        )}

        {/* Data grid card */}
        <Box
          sx={{
            bgcolor: "white",
            border: "1px solid",
            borderColor: "rgba(0, 0, 0, 0.08)",
            borderRadius: 2,
            boxShadow: "0 1px 2px rgba(35, 66, 133, 0.04)",
            overflow: "hidden",
          }}
        >
          <Sl2DataGrid
            datagridProps={{
              columns: columns(t),
              rows: tenantsWithOwners,
              rowSelection: false,
            }}
            pageSize={pageSize}
            listCount={tenants.length}
            onNextTokenChange={() =>
              setParams((prev) => {
                return {
                  ...prev,
                  "ap.nextToken": JSON.stringify(q.data?.meta?.nextToken),
                  timestamp: Date.now(),
                }
              })
            }
            isLoading={q.isLoading || q.isFetching}
          />
        </Box>
      </Box>
      {selectedTenantId && (
        <ObjectDisplayDrawer
          data={tenants.find((t) => t.id === selectedTenantId)}
          title={tenants.find((t) => t.id === selectedTenantId)?.name}
          onClose={() => setSelectedTenantId(null)}
        />
      )}
      <Snackbar
        open={!!snackMessage?.message}
        autoHideDuration={6000}
        onClose={() => setSnackMessage({ message: "", severity: undefined })}
      >
        <Alert severity={snackMessage.severity}>{snackMessage.message}</Alert>
      </Snackbar>
    </SaLayout>
  )
}

export default SaTenantsPage
